import { CITY } from '@city'
import { useEffect, useSyncExternalStore } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSchema } from '../components/useSchema'
import { useMetroMapDispatch, useMetroMapStore } from '../state/useMetroMapStore'
import { lineDiagramFor } from './lineDiagramFor'
import { LineUrlKey } from './LineUrlKey'

/**
 * Both directions of the line view's URL (`/<locale>/lines/<slug>`), the
 * line counterpart of `UrlToStoreSync` + `SelectedStationUrlSync`.
 *
 * URL → store: landing on `/lines/<slug>` (cold load, Google, Back) selects
 * the line once the schema is in; a slug with no line view in this city
 * falls back to the locale map. Back from the line to the bare locale map
 * closes it. A station opened from the line view moves the URL to
 * `/stations/<slug>` but keeps the line selected, so Back (or closing the
 * station) returns here.
 *
 * Store → URL: a line opened from the map (segment tap, station-card link)
 * pushes `/lines/<slug>`; closing it pushes the locale map. A route
 * completed from the line view belongs to `RouteSelectionUrlSync` (`/route`).
 */
export function LineUrlSync(): null {
  const schema = useSchema()
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const navigate = useNavigate()
  const { locale, lineSlug } = useParams<{ locale?: string; lineSlug?: string }>()
  const localePath = locale !== undefined ? `/${locale}` : ''
  const selectedLine = useSyncExternalStore(store.subscribe, () => store.getState().selectedLine)
  const selectedStation = useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedStation,
  )

  useEffect(() => {
    if (schema === null) return
    if (lineSlug === undefined) {
      const path = window.location.pathname
      const onLocaleMap = path === localePath || path === `${localePath}/` || path === '/'
      if (onLocaleMap && store.getState().selectedLine !== null) dispatch({ kind: 'LineCleared' })
      return
    }
    const lineId = LineUrlKey.lineIdOf(schema, lineSlug)
    if (lineId === null || lineDiagramFor(schema, lineId, CITY.lineDiagrams) === null) {
      navigate({ pathname: localePath === '' ? '/' : localePath }, { replace: true })
      return
    }
    dispatch({ kind: 'LineSelected', line: { lineId, slug: lineSlug }, source: 'url' })
  }, [schema, lineSlug, localePath, store, dispatch, navigate])

  useEffect(() => {
    if (schema === null) return
    // Read the store, not the render values: the effect above may have just
    // selected the line from the URL in this same commit.
    const { selectedLine: line, selectedStation: station, routeSelection } = store.getState()
    const path = window.location.pathname
    const linePath = line === null ? null : `${localePath}/lines/${line.slug}`
    if (line !== null) {
      // A station on top of the line owns the URL (`/stations/<slug>`).
      if (station !== null || path === linePath) return
      const search = new URLSearchParams(window.location.search)
      search.delete('station')
      search.delete('line')
      const query = search.toString()
      navigate({ pathname: linePath!, search: query === '' ? '' : `?${query}` })
      return
    }
    // A completed route closed the line: `RouteSelectionUrlSync` moves to `/route`.
    if (routeSelection.fromStation !== null && routeSelection.toStation !== null) return
    if (path.startsWith(`${localePath}/lines/`)) {
      navigate({ pathname: localePath === '' ? '/' : localePath, search: window.location.search })
    }
  }, [schema, selectedLine, selectedStation, localePath, store, navigate])

  return null
}
